/**
 * Date formatting utilities for consistent date/time display across the app
 * Consolidates duplicate date formatting logic from CreditHistory, OrderTable, OrderDetailsModal, etc.
 */

/**
 * Format date as relative time (Today, Yesterday, X days ago) or absolute date
 * @param {string|Date} dateString - Date to format
 * @returns {string} Formatted date string
 */
const formatDate = (dateString) => {
  if (!dateString) return '';

  const date = new Date(dateString);
  const now = new Date();
  const diffTime = Math.abs(now - date);
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

  if (diffDays === 0) {
    return 'Today';
  } else if (diffDays === 1) {
    return 'Yesterday';
  } else if (diffDays < 7) {
    return `${diffDays} days ago`;
  } else {
    return date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
    });
  }
};

/**
 * Format time only (HH:MM format)
 * @param {string|Date} dateString - Date to extract time from
 * @returns {string} Formatted time string (24-hour format)
 */
const formatTime = (dateString) => {
  if (!dateString) return '';

  const date = new Date(dateString);
  return date.toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit'
  });
};

/**
 * Format date as relative time - alias for formatDate for semantic clarity
 * @param {string|Date} dateString - Date to format
 * @returns {string} Formatted relative date string
 */
const formatRelativeDate = (dateString) => {
  return formatDate(dateString);
};

/**
 * Format date and time together with configurable options
 * @param {string|Date} dateString - Date to format
 * @param {Object} options - Formatting options
 * @param {boolean} options.includeSeconds - Include seconds in output (default: false)
 * @param {string} options.monthFormat - 'short' or 'long' (default: 'short')
 * @param {boolean} options.includeYear - Include year (default: true)
 * @returns {string} Formatted date+time string
 */
const formatDateTime = (dateString, options = {}) => {
  if (!dateString) return '';

  const {
    includeSeconds = false,
    monthFormat = 'short',
    includeYear = true
  } = options;

  const date = new Date(dateString);

  const formatOptions = {
    day: 'numeric',
    month: monthFormat,
    hour: '2-digit',
    minute: '2-digit'
  };

  if (includeYear) {
    formatOptions.year = 'numeric';
  }

  if (includeSeconds) {
    formatOptions.second = '2-digit';
  }

  return date.toLocaleString('en-GB', formatOptions);
};

// ES6 exports for React
export { formatDate, formatTime, formatRelativeDate, formatDateTime };
